import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { usePatientContext } from "@/contexts/PatientContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getAnalyticsSummary, getContactMessages, updateContactStatus } from "@/lib/api";
import { formatRelativeTime } from "@/lib/utils";
import { toast } from "sonner";
import AuthModal from "@/components/AuthModal";
import AddToEHR from "@/components/AddToEHR";
import HospiceOSFeatures from "@/components/HospiceOSFeatures";
import CommandPaletteButton from "@/components/CommandPaletteButton";
import { 
  BarChart3, 
  Users, 
  MessageSquare,
  Mail,
  TrendingUp,
  Eye,
  RefreshCw,
  CheckCircle,
  Clock,
  Archive,
  BookMarked,
  Lock,
  Activity,
  Sparkles
} from "lucide-react";

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setViewContext } = usePatientContext();
  const [analytics, setAnalytics] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Set view context for command palette
  useEffect(() => {
    setViewContext("admin_dashboard");
    return () => setViewContext("general");
  }, [setViewContext]);

  useEffect(() => {
    if (!authLoading) {
      if (user?.role === "admin" || user?.role === "administrator") {
        loadData();
      } else {
        setLoading(false);
      }
    }
  }, [user, authLoading]);

  const loadData = async () => {
    try {
      const [analyticsData, contactsData] = await Promise.all([
        getAnalyticsSummary(),
        getContactMessages()
      ]);
      setAnalytics(analyticsData);
      setContacts(contactsData);
    } catch (error) {
      console.error("Error loading data:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handleStatusUpdate = async (messageId, status) => {
    try {
      await updateContactStatus(messageId, status);
      setContacts(prev => prev.map(c => c.id === messageId ? { ...c, status } : c));
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      new: { variant: "default", icon: Clock, label: t('adminDashboard.status.new', 'New') },
      in_progress: { variant: "secondary", icon: RefreshCw, label: t('adminDashboard.status.inProgress', 'In Progress') },
      resolved: { variant: "outline", icon: CheckCircle, label: t('adminDashboard.status.resolved', 'Resolved') },
      archived: { variant: "outline", icon: Archive, label: t('adminDashboard.status.archived', 'Archived') }
    };
    const config = variants[status] || variants.new;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Show loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show auth required message for non-admin users
  if (!user || (user.role !== "admin" && user.role !== "administrator")) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6" data-testid="admin-restricted">
        <Card className="max-w-md w-full text-center shadow-soft">
          <CardContent className="p-8">
            <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-2">{t('adminDashboard.accessRequired', 'Admin Access Required')}</h2>
            <p className="text-muted-foreground mb-6">
              {user 
                ? t('adminDashboard.noPrivileges', 'Your account does not have admin privileges. Please contact an administrator.')
                : t('adminDashboard.pleaseSignIn', 'Please sign in with an admin account to access the dashboard.')
              }
            </p>
            {!user && (
              <Button onClick={() => setShowAuthModal(true)} data-testid="admin-signin-btn">
                {t('adminDashboard.signInAsAdmin', 'Sign In as Admin')}
              </Button>
            )}
          </CardContent>
        </Card>
        <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-dashboard-page">
      {/* Header */}
      <section className="bg-white border-b border-border/50 py-8">
        <div className="container-max px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="heading-2 text-foreground mb-2">{t('adminDashboard.title', 'Admin Dashboard')}</h1>
              <p className="text-muted-foreground">
                {t('adminDashboard.subtitle', 'Monitor site analytics, chat usage, and contact messages')}
              </p>
            </div>
            <div className="flex gap-2">
              {/* Command Palette Quick Access */}
              <CommandPaletteButton 
                variant="header"
                viewContext="admin_dashboard"
                userRole="admin"
                showKeyboardHint={true}
              />
              <AddToEHR 
                buttonVariant="default"
                buttonSize="default"
                buttonText={t('staffPortal.admin.addToEHR', 'Add to EHR')}
                className="bg-teal-600 hover:bg-teal-700"
              />
              <Button 
                variant="outline" 
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {t('adminDashboard.refreshData', 'Refresh Data')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="py-8 px-6 md:px-12" data-testid="stats-section">
        <div className="container-max">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
            <Card className="shadow-soft" data-testid="stat-total-visits">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('adminDashboard.stats.totalVisits', 'Total Page Visits')}</p>
                    <p className="text-3xl font-serif font-semibold text-foreground">
                      {analytics?.total_visits || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft" data-testid="stat-chat-sessions">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('adminDashboard.stats.chatSessions', 'Chat Sessions')}</p>
                    <p className="text-3xl font-serif font-semibold text-foreground">
                      {analytics?.total_chat_sessions || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-secondary-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft" data-testid="stat-total-users">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Registered Users</p>
                    <p className="text-3xl font-serif font-semibold text-foreground">
                      {analytics?.total_users || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-accent-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft" data-testid="stat-journal-entries">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Journal Entries</p>
                    <p className="text-3xl font-serif font-semibold text-foreground">
                      {analytics?.total_journal_entries || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <BookMarked className="w-5 h-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft" data-testid="stat-new-contacts">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">New Messages</p>
                    <p className="text-3xl font-serif font-semibold text-foreground">
                      {analytics?.new_contacts || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Tabs Content */}
      <section className="pb-16 px-6 md:px-12">
        <div className="container-max">
          <Tabs defaultValue="analytics" className="space-y-6" data-testid="admin-tabs">
            <TabsList className="bg-white shadow-soft">
              <TabsTrigger value="analytics" data-testid="tab-analytics">
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts">
                <Mail className="w-4 h-4 mr-2" />
                Contact Messages
              </TabsTrigger>
              <TabsTrigger value="system" data-testid="tab-system">
                <Activity className="w-4 h-4 mr-2" />
                System
              </TabsTrigger>
            </TabsList>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Page Visits */}
                <Card className="shadow-soft" data-testid="page-visits-card">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg">Page Visits</CardTitle>
                    <CardDescription>Visits by page</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analytics?.visits_by_page?.length > 0 ? (
                      <div className="space-y-4">
                        {analytics.visits_by_page.map((item, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <span className="text-foreground capitalize">
                              {item.page === 'home' ? 'Home' : item.page}
                            </span>
                            <div className="flex items-center gap-3">
                              <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary rounded-full"
                                  style={{ 
                                    width: `${Math.min(100, (item.count / (analytics.total_visits || 1)) * 100)}%` 
                                  }}
                                />
                              </div>
                              <span className="text-sm font-medium text-muted-foreground w-8 text-right">
                                {item.count}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-8">No page visits yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Chat by Context */}
                <Card className="shadow-soft" data-testid="chat-context-card">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg">Chat Usage by Topic</CardTitle>
                    <CardDescription>Messages by conversation context</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analytics?.chats_by_context?.length > 0 ? (
                      <div className="space-y-4">
                        {analytics.chats_by_context.map((item, index) => {
                          const contextLabels = {
                            goals_of_care: "Goals of Care",
                            hospice_education: "Hospice Education",
                            caregiver_support: "Caregiver Support",
                            symptom_guide: "Symptom Guide"
                          };
                          return (
                            <div key={index} className="flex items-center justify-between">
                              <span className="text-foreground">
                                {contextLabels[item.context] || item.context}
                              </span>
                              <Badge variant="secondary">{item.count} messages</Badge>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-8">No chat messages yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Daily Visits */}
                <Card className="shadow-soft lg:col-span-2" data-testid="daily-visits-card">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg">Daily Visits (Last 7 Days)</CardTitle>
                    <CardDescription>Visit trends over the past week</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analytics?.daily_visits?.length > 0 ? (
                      <div className="flex items-end gap-2 h-40">
                        {analytics.daily_visits.map((day, index) => {
                          const maxCount = Math.max(...analytics.daily_visits.map(d => d.count));
                          const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                          return (
                            <div key={index} className="flex-1 flex flex-col items-center gap-2">
                              <div 
                                className="w-full bg-primary/80 rounded-t-lg transition-all hover:bg-primary"
                                style={{ height: `${Math.max(height, 5)}%` }}
                                title={`${day.count} visits`}
                              />
                              <span className="text-xs text-muted-foreground">
                                {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-8">No visit data available</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Contacts Tab */}
            <TabsContent value="contacts">
              <Card className="shadow-soft" data-testid="contacts-table-card">
                <CardHeader>
                  <CardTitle className="font-serif text-lg">Contact Messages</CardTitle>
                  <CardDescription>Manage incoming contact form submissions</CardDescription>
                </CardHeader>
                <CardContent>
                  {contacts.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table data-testid="contacts-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Subject</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {contacts.map((contact) => (
                            <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                              <TableCell className="text-muted-foreground">
                                {formatRelativeTime(contact.timestamp)}
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{contact.name}</p>
                                  <p className="text-sm text-muted-foreground">{contact.email}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{contact.subject}</p>
                                  <p className="text-sm text-muted-foreground line-clamp-1">
                                    {contact.message}
                                  </p>
                                </div>
                              </TableCell>
                              <TableCell>
                                {getStatusBadge(contact.status)}
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={contact.status}
                                  onValueChange={(value) => handleStatusUpdate(contact.id, value)}
                                >
                                  <SelectTrigger className="w-32" data-testid={`status-select-${contact.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="new">New</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="resolved">Resolved</SelectItem>
                                    <SelectItem value="archived">Archived</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-12">No contact messages yet</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* System Tab - Hospice OS Features */}
            <TabsContent value="system" data-testid="system-tab-content">
              <HospiceOSFeatures variant="admin" />
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
